import type { ApolloLink } from '@apollo/client';
import { uniq } from 'lodash-es';
import qs from 'qs';

import { canHashSha256, hashSha256 } from '@change-corgi/core/crypto';
import { type ErrorReporter, setErrorSamplingOnResponse } from '@change-corgi/core/errorReporter/common';

import type { WebappInfo } from '../webappInfoLink';

import { isOffNetworkResponse } from './getFetchResponseError';
import { getRequestGqlDetails } from './getRequestGqlDetails';
import { getRequestMethod } from './getRequestMethod';
import { getRequestUrl } from './getRequestUrl';
import { reportResponseError } from './reportResponseError';

async function withGraphqlHashHeader(
	init: RequestInit | undefined,
	webappInfo: WebappInfo | undefined,
): Promise<RequestInit | undefined> {
	if (!webappInfo?.version || !init?.body || typeof init.body !== 'string') {
		return init;
	}
	const initialHeaders = init.headers;
	if (initialHeaders instanceof Headers || Array.isArray(initialHeaders)) {
		// Headers instance not yet supported for server-side fetch
		// Array of headers not yet supported for server-side fetch
		return init;
	}
	// crypto.subtle is unavailable on local-change.org (HTTP, not HTTPS) and within TestCafe.
	// Omitting the header in those environments is not a security issue: the server treats a
	// missing header as an unverifiable request and simply skips the integrity check.
	if (!canHashSha256()) return init;
	const hash = (await hashSha256(`${webappInfo.version}:${init.body}`)).slice(0, 32);
	// eslint-disable-next-line @typescript-eslint/naming-convention
	const headers = { ...initialHeaders, 'X-Change-Graphql-Hash': hash };
	return { ...init, headers };
}

type Options = {
	fetch: typeof fetch;
	errorReporter: ErrorReporter;
	networkErrorReporter: ErrorReporter;
	addOperationNamesToUri?: boolean;
	webappInfo?: WebappInfo;
	/**
	 * sample rate for 504s that did not come through our network (see isOffNetworkResponse)
	 *
	 * the decision is made once per response, and applies to every report of it or of an error made from it
	 *
	 * only for environments behind Cloudfront: elsewhere, every 504 looks like this
	 *
	 * see docs/front-end/errors/error-reporting.md#graphql-504s-from-outside-our-network
	 */
	offNetworkGatewayTimeoutSampleRate?: number;
};

/**
 * `ApolloLink.Request` extended with the operation name, which Apollo's type omits — no idea why.
 */
type GraphQLRequest = ApolloLink.Request & {
	operationName: string;
};

function getOperationNames(bodyJson: BodyInit | undefined | null): readonly string[] {
	if (typeof bodyJson !== 'string') return [];
	try {
		const body = JSON.parse(bodyJson) as GraphQLRequest | GraphQLRequest[];
		const bodyArray = Array.isArray(body) ? body : [body];
		return bodyArray
			.map(({ operationName }) => operationName)
			.filter((operationName): operationName is string => !!operationName);
	} catch (e) {
		return [];
	}
}

function buildUrl(uri: RequestInfo | URL, operationNames: readonly string[] | undefined) {
	if (typeof uri !== 'string' || !operationNames || !operationNames.length) return uri;
	const [url, query] = uri.split('?');
	return `${url}?${qs.stringify(
		{
			...qs.parse(query ?? ''),
			op: uniq(operationNames)
				.sort()
				.map((operationName) => {
					const count = operationNames.filter((name) => name === operationName).length;
					return count > 1 ? `${operationName}~${count}` : operationName;
				}),
		},
		{ arrayFormat: 'repeat' },
	)}`;
}

function validateSampleRate(sampleRate: number | undefined): void {
	if (sampleRate !== undefined && !(sampleRate >= 0 && sampleRate <= 1)) {
		throw new Error('Invalid Sample Rate');
	}
}

/**
 * the decision is stored on the response, so that every report of it uses the same one
 */
function sampleOffNetworkGatewayTimeout(response: Response, sampleRate: number | undefined): void {
	if (sampleRate === undefined || response.status !== 504 || !isOffNetworkResponse(response)) return;
	setErrorSamplingOnResponse(response, { sampleRate, sampled: Math.random() < sampleRate });
}

export function wrapFetch({
	fetch: originalFetch,
	errorReporter,
	networkErrorReporter,
	addOperationNamesToUri,
	webappInfo,
	offNetworkGatewayTimeoutSampleRate,
}: Options): typeof fetch {
	validateSampleRate(offNetworkGatewayTimeoutSampleRate);

	// Patch fetch to ensure error includes relevant debugging info

	return async function fetch(infoParam, init) {
		const info = addOperationNamesToUri ? buildUrl(infoParam, getOperationNames(init?.body)) : infoParam;

		try {
			const result = await originalFetch(info, await withGraphqlHashHeader(init, webappInfo));
			if (!result.ok) {
				// eslint-disable-next-line @typescript-eslint/only-throw-error
				throw result;
			}
			return result;
		} catch (e) {
			if ((e as Response).status) {
				const response = e as Response;

				sampleOffNetworkGatewayTimeout(response, offNetworkGatewayTimeoutSampleRate);
				reportResponseError(init, info, response, errorReporter);

				if (response.status < 500) {
					return response;
				}
				throw e;
			}
			void networkErrorReporter.report({
				error: e as Error,
				params: {
					url: getRequestUrl(info),
					method: getRequestMethod(info, init),
					...getRequestGqlDetails(init),
				},
			});
			throw e;
		}
	};
}
